import React, { useCallback, useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';
import { Link, usePathname, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import { ARTIFACT_NAV_RAIL_WIDTH } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import useKeyboardShortcuts from '@/hooks/useKeyboardShortcuts';
import { useAuthStore, useModalStore, useUIStore } from '@/store';
import { openHomeComposerFromPath } from '@/lib/openHomeComposer';
import SidebarAccountButton from './SidebarAccountButton';
import RailRecentChats from './RailRecentChats';

function RailAction({ label, icon, href, onPress, active, shortcut, colors, children }) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const [focused, setFocused] = useState(false);
  const button = (
    <Pressable
      accessibilityRole={href ? 'link' : 'button'}
      accessibilityLabel={label}
      accessibilityState={href ? { selected: !!active } : undefined}
      aria-current={href && active ? 'page' : undefined}
      onPress={onPress}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      // Expo Link merges its child's style before Pressable evaluates it.
      // Supply an object so link targets retain their size and hover styles.
      style={StyleSheet.flatten([
        styles.action,
        { backgroundColor: active ? colors.solid_color_secondary : 'transparent' },
        (hovered || pressed || focused) && { backgroundColor: colors.solid_color_tertiary },
      ])}
    >
      {(typeof children === 'function' ? children({ hovered, focused }) : children) || <Icon name={icon} size={19} color={active ? colors.text_color_primary : colors.text_color_secondary} />}
    </Pressable>
  );
  return (
    <Tooltip label={label} keyboardShortcut={shortcut} side="right">
      {href ? <Link href={href} asChild withAnchor>{button}</Link> : button}
    </Tooltip>
  );
}

/** Keeps the main navigation within reach while an artifact uses the workspace. */
export default function ArtifactNavigationRail({ pwaSafeAreaTop = 0 }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const pathname = usePathname();
  const router = useRouter();
  const requireAuth = useRequireAuth();
  const hasUser = useAuthStore((state) => Boolean(state.user?.id));
  const openModal = useModalStore((state) => state.actions.openModal);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);

  const handleNewChat = useCallback(() => {
    openHomeComposerFromPath(router, pathname);
  }, [pathname, router]);

  const handleOpenSearch = useCallback(() => {
    requireAuth(() => openModal('SearchModal'), {
      source: 'sidebar_search',
      title: t('sidebar.signInToSaveWork'),
      subtitle: t('sidebar.signInToSearchChatsSubtitle'),
      icon: 'magnifying-glass',
    });
  }, [requireAuth, openModal, t]);
  const shortcutHandlers = useMemo(() => ({ FOCUS_SEARCH: handleOpenSearch }), [handleOpenSearch]);
  useKeyboardShortcuts(shortcutHandlers);

  return (
    <View
      accessibilityRole="navigation"
      accessibilityLabel="Main navigation"
      style={[styles.rail, {
        backgroundColor: colors.solid_color_block,
        borderRightColor: colors.border_color_secondary,
        paddingTop: Math.max(insets.top, pwaSafeAreaTop),
      }]}
    >
      <View style={styles.header}>
        <RailAction label="Open sidebar" onPress={toggleSidebar} shortcut="⌘ ⇧ S" colors={colors}>
          {({ hovered, focused }) => hovered || focused ? (
            <View style={{ width: 22, height: 20, borderWidth: 1.7, borderRadius: 4, borderColor: colors.text_color_primary }}>
              <View style={{ position: 'absolute', left: 6, top: 0, bottom: 0, borderLeftWidth: 1.7, borderColor: colors.text_color_primary }} />
            </View>
          ) : <Logo width={24} height={24} color={colors.text_color_primary} />}
        </RailAction>
      </View>
      <ScrollView style={styles.scroll} contentContainerStyle={styles.actions} showsVerticalScrollIndicator={false}>
        <RailAction label={t('nav.routes.chat.newChat')} icon="pen-to-square" onPress={handleNewChat} colors={colors} />
        <RailAction label={t('navigation.search')} icon="magnifying-glass" onPress={handleOpenSearch} shortcut="⌘ K" colors={colors} />
        <View style={[styles.divider, { backgroundColor: colors.border_color_secondary }]} />
        {hasUser ? <RailRecentChats colors={colors} /> : (
          <RailAction label={t('navigation.library')} icon="messages" href="/library" active={pathname === '/library'} colors={colors} />
        )}
        <RailAction label={t('navigation.files')} icon="files" href="/files" active={pathname === '/files'} colors={colors} />
        <RailAction label={t('navigation.tasks')} icon="clipboard-list" href="/tasks"
          active={pathname === '/tasks' || pathname.startsWith('/tasks/')} colors={colors} />
      </ScrollView>
      {hasUser ? (
        <Tooltip label={t('sidebar.accountMenu')} side="right">
          <View style={styles.account}><SidebarAccountButton compact /></View>
        </Tooltip>
      ) : (
        <View style={[styles.signedOutAccount, { borderTopColor: colors.border_color_secondary, paddingBottom: 8 + insets.bottom }]}>
          <RailAction label={t('sidebar.logIn')} icon="circle-user" onPress={() => openModal('LoginModal')} colors={colors} />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  rail: { width: ARTIFACT_NAV_RAIL_WIDTH, flexShrink: 0, height: '100%', minHeight: 0, borderRightWidth: 1 },
  header: { minHeight: 53, alignItems: 'center', justifyContent: 'center' },
  scroll: { flex: 1, minHeight: 0 },
  actions: { alignItems: 'center', paddingTop: 8, paddingBottom: 16, gap: 6 },
  action: { width: 44, height: 44, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  divider: { width: 28, height: 1, marginVertical: 6 },
  account: { width: ARTIFACT_NAV_RAIL_WIDTH },
  signedOutAccount: { paddingTop: 8, borderTopWidth: 1, alignItems: 'center' },
});
